import { Injectable } from '@angular/core';
import { formatPhoneDisplay } from '../../shared/utils/phone.util';

/**
 * In-memory data store backing the demo mock interceptor.
 *
 * Records are kept in the raw *API* shape (publicId, isEinvoiceEligible, ...)
 * because the real services map that shape into their view DTOs. POSTs append
 * to the store, GET lists read from it — so a record "saved" during the demo
 * immediately appears in the relevant list, exactly like production.
 *
 * Seeded with realistic Turkish records so every flow works standalone (e.g.
 * the invoice flow always has customers and products to pick from).
 */

export interface DemoApiCustomer {
  publicId: string;
  name: string;
  taxId: string | null;
  taxOffice: string | null;
  email: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  stateId: number | null;
  cityId: number | null;
  countryId: number | null;
  postalCode: string | null;
  currencyId: number | null;
  isEinvoiceEligible: boolean;
  isActive: boolean;
  notes: string | null;
  stateName: string | null;
  cityName: string | null;
  currentBalance: number;
  totalBalance: number;
}

export interface DemoApiProduct {
  publicId: string;
  name: string;
  sku: string;
  productUnitName: string;
  productUnitId: number | null;
  defaultVatRate: number;
  unitPrice: number;
  purchasePrice: number | null;
  currencyCode: string;
  currencyId: number | null;
  productType: number;
  defaultDescription: string | null;
  defaultVatExemptionTypeId: number | null;
  defaultWithholdingTypeId: number | null;
  trackInventory: boolean;
  isActive: boolean;
}

export interface DemoApiInvoiceLine {
  publicId: string;
  lineNumber: number;
  productPublicId: string | null;
  productName: string | null;
  description: string;
  quantity: number;
  productUnitId: number | null;
  productUnitCode: string | null;
  unitPrice: number;
  vatRate: number;
  lineSubtotal: number;
  vatAmount: number;
  lineTotal: number;
}

export interface DemoApiInvoice {
  publicId: string;
  customerPublicId: string | null;
  customerName: string;
  invoiceSeriesCode: string | null;
  documentType: number;
  invoiceType: number;
  profile: number;
  status: number;
  issueDate: string;
  issueTime: string | null;
  dueDate: string | null;
  invoiceNo: string | null;
  currencyId: number | null;
  currencyCode: string;
  subtotalAmount: number;
  vatAmount: number;
  totalAmount: number;
  amountPayable: number;
  amountPaid: number;
  amountDue: number;
  paymentStatus: number;
  isCancelled: boolean;
  notes: string | null;
  dtInsert: string;
  dtUpdate: string | null;
  lines: DemoApiInvoiceLine[];
  taxSummaries: unknown[];
}

interface CreateCustomerBody {
  name?: string;
  taxId?: string;
  taxOffice?: string;
  email?: string;
  phone?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  stateId?: number;
  cityId?: number;
  countryId?: number;
  postalCode?: string;
  currencyId?: number;
  isEinvoiceEligible?: boolean;
  notes?: string;
}

interface CreateProductBody {
  name?: string;
  sku?: string;
  unit?: string;
  productUnitId?: number | null;
  defaultVatRate?: number;
  unitPrice?: number;
  purchasePrice?: number | null;
  currencyId?: number | null;
  productType?: number;
  defaultDescription?: string | null;
  defaultVatExemptionTypeId?: number | null;
  defaultWithholdingTypeId?: number | null;
  trackInventory?: boolean;
}

interface CreateInvoiceLineBody {
  productPublicId?: string | null;
  description?: string;
  quantity?: number;
  productUnitId?: number | null;
  unitPrice?: number;
  vatRate?: number;
}

interface CreateInvoiceBody {
  customerPublicId?: string | null;
  issueDate?: string;
  issueTime?: string | null;
  dueDate?: string | null;
  currencyId?: number | null;
  notes?: string | null;
  lines?: CreateInvoiceLineBody[];
}

function currencyCodeFromId(currencyId: number | null | undefined): string {
  switch (currencyId) {
    case 2: return 'USD';
    case 3: return 'EUR';
    default: return 'TRY';
  }
}

@Injectable({ providedIn: 'root' })
export class DemoDataStore {
  private seeded = false;
  private customerSeq = 0;
  private productSeq = 0;
  private invoiceSeq = 0;

  private customers: DemoApiCustomer[] = [];
  private products: DemoApiProduct[] = [];
  private invoices: DemoApiInvoice[] = [];

  /** Reset to the pristine seeded state. Called every time a flow starts. */
  reset(): void {
    this.customerSeq = 0;
    this.productSeq = 0;
    this.invoiceSeq = 0;
    this.customers = [];
    this.products = [];
    this.invoices = [];
    this.seeded = false;
    this.ensureSeeded();
  }

  ensureSeeded(): void {
    if (this.seeded) return;
    this.seeded = true;

    // Generic placeholder companies only — never real business names.
    this.customers = [
      this.makeCustomer({ name: 'Test Teknoloji A.Ş.', taxId: '1234567890', email: 'info@test.example', isEinvoiceEligible: true }),
      this.makeCustomer({ name: 'Örnek Ticaret Ltd. Şti.', taxId: '9876543210', email: 'satis@ornek.example', isEinvoiceEligible: true }),
      this.makeCustomer({ name: 'Example Yazılım A.Ş.', taxId: '4567891230', email: 'muhasebe@example.example', isEinvoiceEligible: false }),
    ];

    this.products = [
      this.makeProduct({ name: 'Web Tasarım Paketi', unitPrice: 12500, defaultVatRate: 20, productType: 2 }),
      this.makeProduct({ name: 'Aylık Bakım Hizmeti', unitPrice: 3500, defaultVatRate: 20, productType: 2 }),
      this.makeProduct({ name: 'Sunucu Kiralama', unitPrice: 1800, defaultVatRate: 20, productType: 2 }),
    ];

    this.invoices = [
      this.makeSeedInvoice('FTR-2026-0001', this.customers[0], this.products[0], 1, '2026-05-21'),
      this.makeSeedInvoice('FTR-2026-0002', this.customers[1], this.products[1], 2, '2026-06-03'),
    ];
  }

  // ---- Customers ---------------------------------------------------------

  listCustomers(search?: string): DemoApiCustomer[] {
    this.ensureSeeded();
    const q = (search ?? '').trim().toLowerCase();
    const rows = q
      ? this.customers.filter((c) => `${c.name} ${c.taxId ?? ''}`.toLowerCase().includes(q))
      : this.customers;
    // Newest first so a freshly created record shows on top.
    return [...rows].reverse();
  }

  getCustomer(publicId: string): DemoApiCustomer | undefined {
    this.ensureSeeded();
    return this.customers.find((c) => c.publicId === publicId);
  }

  addCustomer(body: CreateCustomerBody): DemoApiCustomer {
    this.ensureSeeded();
    const customer = this.makeCustomer({
      name: body.name ?? 'Yeni Müşteri',
      taxId: body.taxId ?? null,
      taxOffice: body.taxOffice ?? null,
      email: body.email ?? null,
      phone: formatPhoneDisplay(body.phonePrefix, body.phoneNumber, body.phone) ?? null,
      addressLine1: body.addressLine1 ?? null,
      countryId: body.countryId ?? 3,
      currencyId: body.currencyId ?? 1,
      isEinvoiceEligible: !!body.isEinvoiceEligible,
      notes: body.notes ?? null,
    });
    this.customers.push(customer);
    return customer;
  }

  // ---- Products ----------------------------------------------------------

  listProducts(search?: string): DemoApiProduct[] {
    this.ensureSeeded();
    const q = (search ?? '').trim().toLowerCase();
    const rows = q
      ? this.products.filter((p) => `${p.name} ${p.sku}`.toLowerCase().includes(q))
      : this.products;
    return [...rows].reverse();
  }

  getProduct(publicId: string): DemoApiProduct | undefined {
    this.ensureSeeded();
    return this.products.find((p) => p.publicId === publicId);
  }

  addProduct(body: CreateProductBody): DemoApiProduct {
    this.ensureSeeded();
    const product = this.makeProduct({
      name: body.name ?? 'Yeni Ürün',
      sku: body.sku ?? undefined,
      productUnitName: body.unit ?? 'Adet',
      productUnitId: body.productUnitId ?? 1,
      defaultVatRate: body.defaultVatRate ?? 20,
      unitPrice: body.unitPrice ?? 0,
      purchasePrice: body.purchasePrice ?? null,
      currencyId: body.currencyId ?? 1,
      productType: body.productType ?? 1,
      defaultDescription: body.defaultDescription ?? null,
      trackInventory: !!body.trackInventory,
    });
    this.products.push(product);
    return product;
  }

  // ---- Invoices ----------------------------------------------------------

  listInvoices(): DemoApiInvoice[] {
    this.ensureSeeded();
    return [...this.invoices].reverse();
  }

  getInvoice(publicId: string): DemoApiInvoice | undefined {
    this.ensureSeeded();
    return this.invoices.find((i) => i.publicId === publicId);
  }

  /** Mark an invoice as a successfully sent & accepted e-Fatura. */
  markSent(publicId: string): DemoApiInvoice | undefined {
    const invoice = this.getInvoice(publicId);
    if (invoice) {
      invoice.profile = 1; // EInvoice
      invoice.status = 7; // Accepted
    }
    return invoice;
  }

  addInvoice(body: CreateInvoiceBody): DemoApiInvoice {
    this.ensureSeeded();
    const customer = body.customerPublicId ? this.getCustomer(body.customerPublicId) : undefined;
    const lines = (body.lines ?? []).map((line, index) => this.makeLine(line, index));
    const subtotal = lines.reduce((sum, l) => sum + l.lineSubtotal, 0);
    const vat = lines.reduce((sum, l) => sum + l.vatAmount, 0);
    const total = subtotal + vat;

    this.invoiceSeq += 1;
    const seqNo = this.invoices.length + 1;
    const invoice: DemoApiInvoice = {
      publicId: `demo-inv-${this.invoiceSeq}-${Date.now()}`,
      customerPublicId: body.customerPublicId ?? null,
      customerName: customer?.name ?? 'Müşteri',
      invoiceSeriesCode: 'FTR',
      documentType: 3,
      invoiceType: 0,
      profile: 0,
      status: 0, // Draft — so the detail page shows "e-Belge Gönder"
      issueDate: body.issueDate ?? new Date().toISOString().slice(0, 10),
      issueTime: body.issueTime ?? '12:00',
      dueDate: body.dueDate ?? null,
      invoiceNo: `FTR-2026-${String(1000 + seqNo).slice(1)}`,
      currencyId: body.currencyId ?? 1,
      currencyCode: currencyCodeFromId(body.currencyId),
      subtotalAmount: round2(subtotal),
      vatAmount: round2(vat),
      totalAmount: round2(total),
      amountPayable: round2(total),
      amountPaid: 0,
      amountDue: round2(total),
      paymentStatus: 0,
      isCancelled: false,
      notes: body.notes ?? null,
      dtInsert: new Date().toISOString(),
      dtUpdate: null,
      lines,
      taxSummaries: [],
    };
    this.invoices.push(invoice);
    return invoice;
  }

  // ---- Builders ----------------------------------------------------------

  private makeCustomer(over: Partial<DemoApiCustomer> & { name: string }): DemoApiCustomer {
    this.customerSeq += 1;
    return {
      publicId: `demo-cus-${this.customerSeq}-${Date.now()}`,
      name: over.name,
      taxId: over.taxId ?? null,
      taxOffice: over.taxOffice ?? 'Merkez',
      email: over.email ?? null,
      phone: over.phone ?? null,
      addressLine1: over.addressLine1 ?? null,
      addressLine2: over.addressLine2 ?? null,
      stateId: over.stateId ?? null,
      cityId: over.cityId ?? null,
      countryId: over.countryId ?? 3,
      postalCode: over.postalCode ?? null,
      currencyId: over.currencyId ?? 1,
      isEinvoiceEligible: over.isEinvoiceEligible ?? false,
      isActive: true,
      notes: over.notes ?? null,
      stateName: over.stateName ?? null,
      cityName: over.cityName ?? null,
      currentBalance: over.currentBalance ?? 0,
      totalBalance: over.totalBalance ?? 0,
    };
  }

  private makeProduct(over: Partial<DemoApiProduct> & { name: string }): DemoApiProduct {
    this.productSeq += 1;
    const sku = over.sku ?? this.skuFromName(over.name, this.productSeq);
    return {
      publicId: `demo-prd-${this.productSeq}-${Date.now()}`,
      name: over.name,
      sku,
      productUnitName: over.productUnitName ?? 'Adet',
      productUnitId: over.productUnitId ?? 1,
      defaultVatRate: over.defaultVatRate ?? 20,
      unitPrice: over.unitPrice ?? 0,
      purchasePrice: over.purchasePrice ?? null,
      currencyCode: currencyCodeFromId(over.currencyId ?? 1),
      currencyId: over.currencyId ?? 1,
      productType: over.productType ?? 1,
      defaultDescription: over.defaultDescription ?? null,
      defaultVatExemptionTypeId: over.defaultVatExemptionTypeId ?? null,
      defaultWithholdingTypeId: over.defaultWithholdingTypeId ?? null,
      trackInventory: over.trackInventory ?? false,
      isActive: true,
    };
  }

  private makeLine(line: CreateInvoiceLineBody, index: number): DemoApiInvoiceLine {
    const product = line.productPublicId ? this.getProduct(line.productPublicId) : undefined;
    const quantity = Number(line.quantity ?? 1);
    const unitPrice = Number(line.unitPrice ?? 0);
    const vatRate = Number(line.vatRate ?? 20);
    const lineSubtotal = quantity * unitPrice;
    const vatAmount = (lineSubtotal * vatRate) / 100;
    return {
      publicId: `demo-line-${index + 1}-${Date.now()}`,
      lineNumber: index + 1,
      productPublicId: line.productPublicId ?? null,
      productName: product?.name ?? line.description ?? null,
      description: line.description ?? product?.name ?? '',
      quantity,
      productUnitId: line.productUnitId ?? product?.productUnitId ?? 1,
      productUnitCode: product?.productUnitName ?? 'Adet',
      unitPrice,
      vatRate,
      lineSubtotal: round2(lineSubtotal),
      vatAmount: round2(vatAmount),
      lineTotal: round2(lineSubtotal + vatAmount),
    };
  }

  private makeSeedInvoice(
    invoiceNo: string,
    customer: DemoApiCustomer,
    product: DemoApiProduct,
    quantity: number,
    issueDate: string,
  ): DemoApiInvoice {
    const line = this.makeLine(
      { productPublicId: product.publicId, description: product.name, quantity, unitPrice: product.unitPrice, vatRate: product.defaultVatRate },
      0,
    );
    const total = line.lineTotal;
    this.invoiceSeq += 1;
    return {
      publicId: `demo-inv-seed-${this.invoiceSeq}`,
      customerPublicId: customer.publicId,
      customerName: customer.name,
      invoiceSeriesCode: 'FTR',
      documentType: 3,
      invoiceType: 0,
      profile: 1,
      status: 7,
      issueDate,
      issueTime: '10:30',
      dueDate: issueDate,
      invoiceNo,
      currencyId: 1,
      currencyCode: 'TRY',
      subtotalAmount: line.lineSubtotal,
      vatAmount: line.vatAmount,
      totalAmount: total,
      amountPayable: total,
      amountPaid: 0,
      amountDue: total,
      paymentStatus: 0,
      isCancelled: false,
      notes: null,
      dtInsert: `${issueDate}T10:30:00Z`,
      dtUpdate: null,
      lines: [line],
      taxSummaries: [],
    };
  }

  private skuFromName(name: string, seq: number): string {
    const base = name
      .replace(/[aeıioöuüAEIİOÖUÜ\s]/g, '')
      .toUpperCase()
      .slice(0, 8);
    return base ? `${base}${seq}` : `URN${seq}`;
  }
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
